import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { isEmpty } from "lodash";
import clsx from "clsx";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Alert, Button, Collapse, Form, FormControlLabel, Box, Switch, TextField } from "@curves/ui-base";
import { EmployeeSelect } from "@icoach/components/";
import { ReviewOrderCard } from "@icoach/accountBook/components/CreditOrderList";
import { formatCurrencyFn, isEqual, refIsRequiredError } from "@util/utils";
import useOrderApi from "@apis/useOrderApi";
import {
	InvoiceByCarrierNo,
	InvoiceByCarrierType,
	InvoiceByDonate,
	InvoiceByEmail,
	InvoiceByIssuingType,
	InvoiceByManual,
	InvoiceByTriplicateUniform,
	SynchronizeMemberInvoice,
} from "@icoach/components/invoice";
import { PaidList } from "@icoach/documents/components";
import { PaidListLayout } from "@icoach/documents/components/PaidList";
import { ORDER_INVOICE_ISSUING_TYPE, INVOICE_CARRIER_TYPE } from "@icoach/constants/dataEnums";

const OrderPaymentInfoCard = props => {
	const { sourceData = {} } = props;
	const { cashPayment = 0, cardPayment = 0 } = sourceData;
	return (
		<PaidListLayout className="checkout-box">
			<PaidList.PaidListGroup>
				<PaidList.PaidListItem label="現金收款金額">
					<Box component="strong">{formatCurrencyFn(cashPayment)}</Box>
				</PaidList.PaidListItem>
				<PaidList.PaidListItem label="信用卡收款金額">
					<Box component="strong">{formatCurrencyFn(cardPayment)}</Box>
				</PaidList.PaidListItem>
			</PaidList.PaidListGroup>
		</PaidListLayout>
	);
};

const CheckoutOrderCard = props => {
	const { sourceData = {} } = props;
	const { orders, cashPayment, cardPayment } = sourceData;
	return (
		<React.Fragment>
			{!isEmpty(orders) &&
				orders.map(item => {
					return <ReviewOrderCard key={item.orderID} className="mb-4" sourceData={item} />;
				})}
			<Grid spacing={3} container>
				<Grid className="ml-auto" xs={6} item>
					<OrderPaymentInfoCard sourceData={{ cashPayment, cardPayment }} />
				</Grid>
			</Grid>
		</React.Fragment>
	);
};

const InvoiceGroup = React.forwardRef((props, ref) => {
	const { className, sourceData = {}, errorModule, options = {}, readOnly = false } = props;
	const { issuingType: issuingTypeProps, invoiceCarrierType: invoiceCarrierTypeProps, invoiceCarrierNo, donationCode, invoiceNo, issuingEmail } = sourceData || {};
	const { invoiceTypeOptions = [], invoiceCarrierTypeOptions = [] } = options;
	const [issuingType, setIssuingType] = useState(issuingTypeProps || 2);
	const [invoiceCarrierType, setInvoiceCarrierType] = useState(invoiceCarrierTypeProps || 1);
	const issuingTypeRef = useRef(null);
	const donateRef = useRef(null);
	const invoiceCarrierTypeRef = useRef(null);
	const invoiceCarrierNoRef = useRef(null);
	const triplicateUniformRef = useRef(null);
	const manualRef = useRef(null);
	const emailRef = useRef(null);
	const SynchronizeMemberInvoiceRef = useRef(null);

	const handleChangeIssuingType = value => {
		setIssuingType(value);
	};

	const handleChangeInvoiceCarrierType = value => {
		setInvoiceCarrierType(value);
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};

				if (issuingTypeRef.current && issuingTypeRef.current.getResult) {
					result = Object.assign(result, issuingTypeRef.current.getResult());
				}

				if (donateRef.current && donateRef.current.getResult) {
					result = Object.assign(result, donateRef.current.getResult());
				}

				if (invoiceCarrierTypeRef.current && invoiceCarrierTypeRef.current.getResult) {
					const { carrierType } = invoiceCarrierTypeRef.current.getResult();
					Object.assign(result, { invoiceCarrierType: carrierType });
				}

				if (invoiceCarrierNoRef.current && invoiceCarrierNoRef.current.getResult) {
					const { carrierNo } = invoiceCarrierNoRef.current.getResult();
					Object.assign(result, { invoiceCarrierNo: carrierNo });
				}

				if (triplicateUniformRef.current && triplicateUniformRef.current.getResult) {
					result = Object.assign(result, triplicateUniformRef.current.getResult());
				}

				if (manualRef.current && manualRef.current.getResult) {
					result = Object.assign(result, manualRef.current.getResult());
				}

				if (emailRef.current && emailRef.current.getResult) {
					result = Object.assign(result, emailRef.current.getResult());
				}

				if (SynchronizeMemberInvoiceRef.current && SynchronizeMemberInvoiceRef.current.getResult) {
					result = Object.assign(result, SynchronizeMemberInvoiceRef.current.getResult());
				}

				return result;
			},
			isError: () => refIsRequiredError(issuingTypeRef, donateRef, invoiceCarrierTypeRef, invoiceCarrierNoRef, triplicateUniformRef, manualRef, emailRef),
		}),
		// eslint-disable-next-line
		[],
	);

	useEffect(
		() => {
			if (issuingTypeProps) setIssuingType(issuingTypeProps);
		},
		// eslint-disable-next-line
		[issuingTypeProps],
	);

	useEffect(
		() => {
			if (invoiceCarrierTypeProps) setInvoiceCarrierType(invoiceCarrierTypeProps);
		},
		// eslint-disable-next-line
		[invoiceCarrierTypeProps],
	);

	return (
		<Grid key={issuingTypeProps} className={className} spacing={2} container>
			<Grid xs={12} item>
				<InvoiceByIssuingType ref={issuingTypeRef} issuingType={issuingTypeProps} options={invoiceTypeOptions} errorData={errorModule} onChange={handleChangeIssuingType} readOnly={readOnly} />
			</Grid>
			{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.DONATE) && (
				<Grid xs={12} item>
					<InvoiceByDonate ref={donateRef} donationCode={donationCode} errorData={errorModule} readOnly={readOnly} />
				</Grid>
			)}
			{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE) && (
				<React.Fragment>
					<Grid xs={12} item>
						<InvoiceByCarrierType
							ref={invoiceCarrierTypeRef}
							carrierType={invoiceCarrierType}
							options={invoiceCarrierTypeOptions}
							errorData={errorModule}
							onChange={handleChangeInvoiceCarrierType}
							readOnly={readOnly}
						/>
					</Grid>
					{(isEqual(invoiceCarrierType, INVOICE_CARRIER_TYPE.CITIZENDIGITALCERTIFICATE) || isEqual(invoiceCarrierType, INVOICE_CARRIER_TYPE.MOBILEBARCODE)) && (
						<Grid xs={12} item>
							<InvoiceByCarrierNo ref={invoiceCarrierNoRef} carrierNo={invoiceCarrierNo} carrierType={invoiceCarrierType} errorData={errorModule} readOnly={readOnly} />
						</Grid>
					)}
				</React.Fragment>
			)}
			{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.TRIPLICATEUNIFORMINVOICE) && (
				<InvoiceByTriplicateUniform ref={triplicateUniformRef} taxID={sourceData?.taxID} taxTitle={sourceData?.taxTitle} errorData={errorModule} readOnly={readOnly} />
			)}
			{isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.MANUAL) && (
				<Grid xs={12} item>
					<InvoiceByManual ref={manualRef} invoiceNo={invoiceNo} errorData={errorModule} readOnly={readOnly} />
				</Grid>
			)}

			{!isEqual(issuingType, ORDER_INVOICE_ISSUING_TYPE.MANUAL) && (
				<React.Fragment>
					<Grid item xs={12}>
						<InvoiceByEmail ref={emailRef} issuingEmail={issuingEmail} errorData={errorModule} readOnly={readOnly} />
					</Grid>
					<Grid xs={12} item>
						<SynchronizeMemberInvoice ref={SynchronizeMemberInvoiceRef} />
					</Grid>
				</React.Fragment>
			)}
		</Grid>
	);
});

const ResetOrderInvoiceDialogContent = React.forwardRef((props, ref) => {
	const { sourceData = {} } = props;
	const { invoiceExceptionMessage, coachOptions: employeeOptions, invoiceTypeOptions, invoiceCarrierTypeOptions, isIssued, tipText } = sourceData;
	const [showOrder, setShowOrder] = useState(false);
	const invoiceRef = useRef(null);
	const employeeRef = useRef(null);
	const notesRef = useRef(null);

	// 顯示/隱藏訂單資訊
	const handleStateChange = (_, value) => {
		setShowOrder(value);
	};

	const getResult = () => {
		let result = {};
		if (invoiceRef.current && invoiceRef.current.getResult) {
			result = Object.assign(result, invoiceRef.current.getResult());
		}
		if (employeeRef.current && employeeRef.current.getResult) {
			result = Object.assign(result, { employeeID: employeeRef.current.getResult() });
		}
		result = Object.assign(result, { notes: notesRef.current.getResult() });
		result = Object.assign(result, { isIssued });
		return result;
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(invoiceRef, employeeRef),
		}),
		// eslint-disable-next-line
		[isIssued],
	);

	const alertStyles = isIssued ? "info" : "error";

	return (
		<DialogContent>
			<Alert className="mb-3" severity={alertStyles} variant="filled" color={alertStyles}>
				{isIssued ? tipText : invoiceExceptionMessage}
			</Alert>
			<Grid className="mb-3" spacing={3} container>
				<Grid xs={2} item />
				<Grid xs={8} item>
					<Grid spacing={3} container>
						<Grid xs={12} item>
							<InvoiceGroup ref={invoiceRef} sourceData={sourceData} options={{ invoiceTypeOptions, invoiceCarrierTypeOptions }} />
						</Grid>
						<Grid xs={12} item>
							<EmployeeSelect ref={employeeRef} options={employeeOptions} label="擔當" fullWidth required />
						</Grid>
						<Grid xs={12} item>
							<TextField ref={notesRef} label="備註" rows={4} multiline fullWidth />
						</Grid>
						<Grid xs={12} item>
							<FormControlLabel
								className="ml-0 mb-3"
								control={<Switch defaultChecked={showOrder} onChange={handleStateChange} />}
								prefixLabel={showOrder ? "隱藏訂單資訊" : "顯示訂單資訊"}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Collapse in={showOrder}>
				<CheckoutOrderCard sourceData={sourceData} />
			</Collapse>
		</DialogContent>
	);
});

const ResetOrderInvoiceDialog = props => {
	const { className, open, sourceData: sourceDataProps = {}, onClose, refresh } = props;
	const orderID = sourceDataProps?.orderID;
	const { getOrderReissuingApi, postOrderReissuingApi } = useOrderApi();
	const [sourceData, setSourceData] = useState({});
	const contentRef = useRef();

	const doGetOrderReissuingApi = async orderID => {
		let res = await getOrderReissuingApi(orderID);
		if (res) {
			setSourceData(res);
		}
	};

	const doPostOrderReissuingApi = async (orderID, params) => {
		let res = await postOrderReissuingApi(orderID, params);
		if (res && !res.isError) {
			refresh && refresh(orderID);
			onClose();
		}
	};

	const getParams = () => {
		let result = {
			orderID,
			syncUpgradeMemberEmail: false,
			syncUpgradeMemberCarrier: false,
		};
		if (contentRef.current && contentRef.current.getResult) {
			result = Object.assign(result, contentRef.current.getResult());
		}
		return result;
	};

	const handleSubmit = () => {
		const isError = refIsRequiredError(contentRef);
		if (!isError) {
			const params = getParams();
			doPostOrderReissuingApi(orderID, params);
		}
	};

	useEffect(
		() => {
			if (open && orderID) {
				doGetOrderReissuingApi(orderID);
			}
			return () => setSourceData({});
		},
		// eslint-disable-next-line
		[open, orderID],
	);

	return (
		<Dialog
			className={clsx("reset-order-invoice-dialog", "simple-content", "dialog", className)}
			PaperProps={{ className: "wx-90rem", component: Form }}
			open={open}
			onSubmit={handleSubmit}
			scroll="paper"
			fullWidth
		>
			<GradientColorHeader onClose={onClose}>重新設定發票</GradientColorHeader>
			<ResetOrderInvoiceDialogContent ref={contentRef} sourceData={sourceData} />
			<DialogActions>
				<Button type="submit" variant="contained">
					確認送出
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ResetOrderInvoiceDialog;
